import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { 
  User as FirebaseUser,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth } from '../firebase';
import { signUp as apiSignUp } from '../api/auth';
import { getUsersByUid } from '../api/users';
import { getWorkspaceStats, getMockWorkspaceStats, getWorkspaceById, recalculateWorkspaceStats, updateWorkspaceOnboardingStep } from '../api/workspaces';
import { normalizeOnboardingStep } from '../utils/onboardingStep';
import { mockWorkspaceStats } from '../api/workspaces';
import { User } from '../types/users';
import { WorkspaceStats } from '../types/workspaces';
import { isDemoWorkspace } from '../utils/apiHelpers';
import i18n from '../i18n';

interface AuthContextType {
  firebaseUser: FirebaseUser | null;
  firestoreUser: User | null;
  workspaceStats: WorkspaceStats | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string, phoneNumber?: string) => Promise<void>;
  signOut: () => Promise<void>;
  setFirestoreUser: (user: User | null) => Promise<void>;
  getProfilePicture: (url: string) => Promise<string>;
  refreshWorkspaceStats: () => Promise<void>;
  recalculateAndRefreshStats: () => Promise<void>;
}

// Cache for profile pictures with metadata for cleanup
interface ProfilePictureCacheEntry {
  objectUrl: string;
  timestamp: number;
}

const profilePictureCache = new Map<string, ProfilePictureCacheEntry>();
const CACHE_EXPIRY_TIME = 30 * 60 * 1000; // 30 minutes

const AuthContext = createContext<AuthContextType>({
  firebaseUser: null,
  firestoreUser: null,
  workspaceStats: null,
  loading: true,
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  setFirestoreUser: async () => {},
  getProfilePicture: async () => '',
  refreshWorkspaceStats: async () => {},
  recalculateAndRefreshStats: async () => {},
});

const SELECTED_USER_ID_KEY = 'convive_selected_user_id';

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [firestoreUser, setFirestoreUser] = useState<User | null>(null);
  const [workspaceStats, setWorkspaceStats] = useState<WorkspaceStats | null>(null);
  const [loading, setLoading] = useState(true);

  // Function to clean up expired cache entries
  const cleanupExpiredCache = () => {
    const now = Date.now();
    for (const [url, entry] of profilePictureCache.entries()) {
      if (now - entry.timestamp > CACHE_EXPIRY_TIME) {
        URL.revokeObjectURL(entry.objectUrl);
        profilePictureCache.delete(url);
      }
    }
  };

  // Function to fetch and cache profile picture
  const getProfilePicture = async (url: string): Promise<string> => {
    if (!url || typeof url !== 'string') {
      console.warn('Invalid profile picture URL provided:', url);
      return '';
    }
    
    // Clean up expired cache entries
    cleanupExpiredCache();
    
    // Return cached version if available and not expired
    const cachedEntry = profilePictureCache.get(url);
    if (cachedEntry && (Date.now() - cachedEntry.timestamp) < CACHE_EXPIRY_TIME) {
      return cachedEntry.objectUrl;
    }

    try {
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      
      // Validate that it's an image
      if (!blob.type.startsWith('image/')) {
        throw new Error('File is not an image');
      }
      
      const objectUrl = URL.createObjectURL(blob);
      
      // Cache the object URL with timestamp
      profilePictureCache.set(url, {
        objectUrl,
        timestamp: Date.now()
      });
      
      return objectUrl;
    } catch (error) {
      console.error('Error loading profile picture:', error);
      return '';
    }
  };

  // Function to get cached user data from localStorage
  const getCachedUserData = (uid: string): User | null => {
    try {
      const cached = localStorage.getItem(`user_${uid}`);
      if (cached) {
        const userData = JSON.parse(cached);
        // Check if cache is not older than 24 hours
        const cacheTime = userData._cachedAt;
        const now = Date.now();
        if (now - cacheTime < 24 * 60 * 60 * 1000) { // 24 hours
          return userData;
        } else {
          localStorage.removeItem(`user_${uid}`);
        }
      }
    } catch (error) {
      console.error('Error reading cached user data:', error);
    }
    return null;
  };

  // Function to cache user data in localStorage
  const cacheUserData = (uid: string, userData: User) => {
    try {
      const dataToCache = {
        ...userData,
        _cachedAt: Date.now()
      };
      localStorage.setItem(`user_${uid}`, JSON.stringify(dataToCache));
    } catch (error) {
      console.error('Error caching user data:', error);
    }
  };

  // Function to get cached workspace stats from localStorage
  const getCachedWorkspaceStats = (workspaceId: string): WorkspaceStats | null => {
    try {
      const cached = localStorage.getItem(`workspace_stats_${workspaceId}`);
      if (cached) {
        const stats = JSON.parse(cached);
        // Check if cache is not older than 1 hour
        const cacheTime = stats._cachedAt;
        const now = Date.now();
        if (now - cacheTime < 60 * 60 * 1000) { // 1 hour
          return stats;
        } else {
          localStorage.removeItem(`workspace_stats_${workspaceId}`);
        }
      }
    } catch (error) {
      console.error('Error reading cached workspace stats:', error);
    }
    return null;
  };

  // Function to cache workspace stats in localStorage
  const cacheWorkspaceStats = (workspaceId: string, stats: WorkspaceStats) => {
    try {
      const dataToCache = {
        ...stats,
        _cachedAt: Date.now()
      };
      localStorage.setItem(`workspace_stats_${workspaceId}`, JSON.stringify(dataToCache));
    } catch (error) {
      console.error('Error caching workspace stats:', error);
    }
  };

  // Function to fetch workspace stats
  const fetchWorkspaceStats = async (workspaceId: string): Promise<WorkspaceStats | null> => {
    try {
      // Create a minimal user object with workspace info for the API call
      const mockUser = { workspace: { id: workspaceId } } as User;
      const stats = await getWorkspaceStats(mockUser);
      return stats;
    } catch (error) {
      console.error('Error fetching workspace stats:', error);
      return null;
    }
  };

  // Function to initialize language from user data
  const initializeLanguage = (userData: User) => {
    if (userData?.language && userData.language !== i18n.language) {
      i18n.changeLanguage(userData.language);
    }
  };

  // Function to refresh workspace stats and workspace data
  const refreshWorkspaceStats = useCallback(async () => {
    if (!firestoreUser?.workspace?.id) return;
    
    try {
      // Always fetch the latest stats from Firestore (real calculated data)
      const stats = await getWorkspaceStats(firestoreUser);
      if (stats) {
        setWorkspaceStats(stats);
        // Cache the updated stats
        cacheWorkspaceStats(firestoreUser.workspace.id, stats);
      }
      
      // Get current workspace data
      const fullWorkspace = await getWorkspaceById(firestoreUser.workspace.id);
      
      if (fullWorkspace && firestoreUser) {
        // Update the workspace object with current data
        const updatedUser = {
          ...firestoreUser,
          workspace: {
            ...firestoreUser.workspace,
            language: fullWorkspace.language,
            currency: fullWorkspace.currency,
            status: fullWorkspace.status,
            onboardingStep: fullWorkspace.onboardingStep
          }
        };
        setFirestoreUser(updatedUser);
      }
    } catch (error) {
      console.error('Error refreshing workspace stats:', error);
    }
  }, [firestoreUser?.workspace?.id]);


  // Function to recalculate workspace stats and refresh the context
  const recalculateAndRefreshStats = useCallback(async () => {
    if (!firestoreUser?.workspace?.id) return;
    
    try {
      // Recalculate workspace stats in Firestore
      await recalculateWorkspaceStats(firestoreUser);
      
      // Refresh the stats in context
      await refreshWorkspaceStats();
      
      // Get the latest workspace data to determine onboarding step
      const fullWorkspace = await getWorkspaceById(firestoreUser.workspace.id);
      
      // Get the latest workspace stats after recalculation
      const latestStats = await getWorkspaceStats(firestoreUser);
      
      // Update the local workspace stats with the latest calculated data
      if (latestStats) {
        setWorkspaceStats(latestStats);
        cacheWorkspaceStats(firestoreUser.workspace.id, latestStats);
      }
      
      if (fullWorkspace && latestStats) {
        // Canonical onboarding: 0 blocks, 1 residents, 2 invitation, 3 complete
        let detectedStep = 0;
        const hasBlocks = latestStats.blocks && Object.keys(latestStats.blocks).length > 0;

        if (!hasBlocks) {
          detectedStep = 0;
        } else {
          const totalResidents = latestStats.totalResidents || 0;
          const totalApartmentUnits = Object.values(latestStats.blocks).reduce(
            (total, blockStats) => total + (blockStats.totalApartmentUnits || 0),
            0
          );

          console.log('Onboarding step detection:', {
            totalResidents,
            totalApartmentUnits,
            blocksCount: Object.keys(latestStats.blocks).length
          });

          if (totalApartmentUnits > 0 && totalResidents >= totalApartmentUnits) {
            detectedStep = 2;
          } else {
            detectedStep = 1;
          }
        }

        const currentStep = normalizeOnboardingStep(fullWorkspace.onboardingStep);
        if (detectedStep !== currentStep) {
          console.log(`Updating onboarding step from ${currentStep} to ${detectedStep} based on workspace stats`);
          await updateWorkspaceOnboardingStep(firestoreUser, detectedStep);
          
          // Update the firestoreUser with the new onboardingStep
          const updatedUser = {
            ...firestoreUser,
            workspace: {
              ...firestoreUser.workspace,
              language: fullWorkspace.language,
              currency: fullWorkspace.currency,
              status: fullWorkspace.status,
              onboardingStep: detectedStep
            }
          };
          setFirestoreUser(updatedUser);
        }
      }
      
      console.log('Workspace stats recalculated and refreshed successfully');
    } catch (error) {
      console.error('Error recalculating workspace stats:', error);
      throw error;
    }
  }, [firestoreUser, refreshWorkspaceStats, workspaceStats]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);
      setLoading(true);
      
      if (fbUser) {
        try {
          // Try to get cached user data first (only use if it has a workspace)
          const cachedUserData = getCachedUserData(fbUser.uid);
          
          if (cachedUserData?.workspace?.id) {
            // Enhance cached user data with full workspace information
            if (cachedUserData?.workspace?.id) {
              try {
                const fullWorkspace = await getWorkspaceById(cachedUserData.workspace.id);
                if (fullWorkspace) {
                  // Update the workspace object with full data including language
                  cachedUserData.workspace = {
                    ...cachedUserData.workspace,
                    language: fullWorkspace.language,
                    currency: fullWorkspace.currency,
                    status: fullWorkspace.status,
                    onboardingStep: fullWorkspace.onboardingStep
                  };
                }
              } catch (error) {
                console.warn('Failed to fetch full workspace data for cached user:', error);
              }
            }
            
            setFirestoreUser(cachedUserData);
            
            // Initialize language from user data
            initializeLanguage(cachedUserData);
            
            // Handle workspace stats for demo workspace
            if (cachedUserData.workspace?.id) {
              if (isDemoWorkspace(cachedUserData)) {
                // For demo workspace, use mock stats directly
                try {
                  const mockStats = await getMockWorkspaceStats();
                  setWorkspaceStats(mockStats);
                  cacheWorkspaceStats(cachedUserData.workspace.id, mockStats);
                } catch (error) {
                  console.warn('Failed to load mock workspace stats, using fallback:', error);
                  setWorkspaceStats(mockWorkspaceStats);
                  cacheWorkspaceStats(cachedUserData.workspace.id, mockWorkspaceStats);
                }
              } else {
                // Try to get cached workspace stats
                const cachedStats = getCachedWorkspaceStats(cachedUserData.workspace.id);
                if (cachedStats) {
                  setWorkspaceStats(cachedStats);
                } else {
                  // Fetch fresh workspace stats if not cached
                  const stats = await fetchWorkspaceStats(cachedUserData.workspace.id);
                  if (stats) {
                    setWorkspaceStats(stats);
                    cacheWorkspaceStats(cachedUserData.workspace.id, stats);
                  }
                }
              }
            }
            
            // Pre-cache the user's profile picture if it exists
            if (cachedUserData?.profilePicture?.url) {
              getProfilePicture(cachedUserData.profilePicture.url).catch(error => {
                console.warn('Failed to pre-cache profile picture:', error);
              });
            }
          } else {
            // Fetch all profiles for this user (one per workspace; new users may have one with no workspace)
            const profiles = await getUsersByUid(fbUser.uid);
            const withWorkspace = profiles.filter((p) => p.workspace?.id);
            const lastSelectedId = typeof localStorage !== 'undefined' ? localStorage.getItem(SELECTED_USER_ID_KEY) : null;
            const selectedFromStorage = lastSelectedId ? profiles.find((p) => p.id === lastSelectedId) : null;
            const userData =
              selectedFromStorage?.workspace?.id
                ? selectedFromStorage
                : withWorkspace.length === 1
                  ? withWorkspace[0]
                  : withWorkspace.length > 1
                    ? withWorkspace[0]
                    : profiles[0] ?? null;

            if (userData?.workspace?.id) {
              try {
                const fullWorkspace = await getWorkspaceById(userData.workspace.id);
                if (fullWorkspace) {
                  userData.workspace = {
                    ...userData.workspace,
                    language: fullWorkspace.language,
                    currency: fullWorkspace.currency,
                    status: fullWorkspace.status,
                    onboardingStep: fullWorkspace.onboardingStep
                  };
                }
              } catch (error) {
                console.warn('Failed to fetch full workspace data for user:', error);
              }
              setFirestoreUser(userData);
              initializeLanguage(userData);
              cacheUserData(fbUser.uid, userData);
              if (typeof localStorage !== 'undefined') localStorage.setItem(SELECTED_USER_ID_KEY, userData.id);
              if (isDemoWorkspace(userData)) {
                try {
                  const mockStats = await getMockWorkspaceStats();
                  setWorkspaceStats(mockStats);
                  cacheWorkspaceStats(userData.workspace.id, mockStats);
                } catch (error) {
                  console.warn('Failed to load mock workspace stats, using fallback:', error);
                  setWorkspaceStats(mockWorkspaceStats);
                  cacheWorkspaceStats(userData.workspace.id, mockWorkspaceStats);
                }
              } else {
                const stats = await fetchWorkspaceStats(userData.workspace.id);
                if (stats) {
                  setWorkspaceStats(stats);
                  cacheWorkspaceStats(userData.workspace.id, stats);
                }
              }
              if (userData?.profilePicture?.url) {
                getProfilePicture(userData.profilePicture.url).catch(error => {
                  console.warn('Failed to pre-cache profile picture:', error);
                });
              }
            } else {
              setFirestoreUser(null);
            }
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setFirestoreUser(null);
        }
      } else {
        setFirestoreUser(null);
        setWorkspaceStats(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signUp = async (email: string, password: string, displayName: string, phoneNumber?: string) => {
    await apiSignUp(email, password, displayName, phoneNumber);
  };

  const setFirestoreUserAndLoad = async (user: User | null) => {
    setFirestoreUser(user);
    if (!user) {
      setWorkspaceStats(null);
      return;
    }
    if (typeof localStorage !== 'undefined') localStorage.setItem(SELECTED_USER_ID_KEY, user.id);
    cacheUserData(user.uid!, user);
    initializeLanguage(user);
    if (user.workspace?.id) {
      if (isDemoWorkspace(user)) {
        try {
          const mockStats = await getMockWorkspaceStats();
          setWorkspaceStats(mockStats);
          cacheWorkspaceStats(user.workspace.id, mockStats);
        } catch {
          setWorkspaceStats(mockWorkspaceStats);
          cacheWorkspaceStats(user.workspace.id, mockWorkspaceStats);
        }
      } else {
        const stats = await fetchWorkspaceStats(user.workspace.id);
        if (stats) {
          setWorkspaceStats(stats);
          cacheWorkspaceStats(user.workspace.id, stats);
        }
      }
      if (user.profilePicture?.url) {
        getProfilePicture(user.profilePicture.url).catch(() => {});
      }
    }
  };

  const signOut = async () => {
    for (const entry of profilePictureCache.values()) {
      URL.revokeObjectURL(entry.objectUrl);
    }
    profilePictureCache.clear();
    if (firebaseUser) {
      localStorage.removeItem(`user_${firebaseUser.uid}`);
    }
    if (typeof localStorage !== 'undefined') localStorage.removeItem(SELECTED_USER_ID_KEY);
    if (firestoreUser?.workspace?.id) {
      localStorage.removeItem(`workspace_stats_${firestoreUser.workspace.id}`);
    }
    await firebaseSignOut(auth);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up all cached object URLs when component unmounts
      for (const entry of profilePictureCache.values()) {
        URL.revokeObjectURL(entry.objectUrl);
      }
      profilePictureCache.clear();
    };
  }, []);

  const value = {
    firebaseUser,
    firestoreUser,
    workspaceStats,
    loading,
    signIn,
    signUp,
    signOut,
    setFirestoreUser: setFirestoreUserAndLoad,
    getProfilePicture,
    refreshWorkspaceStats,
    recalculateAndRefreshStats,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 